import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { API, authedHeaders } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, Eye, Megaphone, ShieldAlert, ShieldX,
  Trash2, History, Volume2, VolumeX,
} from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const ago = (iso) => {
  if (!iso) return "—";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return `${Math.round(d)}s ago`;
  if (d < 3600) return `${Math.round(d / 60)}m ago`;
  if (d < 86400) return `${Math.round(d / 3600)}h ago`;
  return `${Math.round(d / 86400)}d ago`;
};

// --------------------------------------------------------------------------- //
//  Live ops panel                                                             //
// --------------------------------------------------------------------------- //
export function LivePanel() {
  const [data, setData] = useState(null);

  const fetchLive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/live`, { headers: authedHeaders() });
      setData(data);
    } catch (e) {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchLive();
    const t = setInterval(fetchLive, 5000);
    return () => clearInterval(t);
  }, [fetchLive]);

  if (!data) return <p className="text-sm text-muted-foreground">Loading live data…</p>;

  return (
    <div data-testid="admin-live-panel" className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Online now</p>
        <p className="font-mono font-semibold text-3xl mt-1">{data.online_total}</p>
      </Card>
      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Active PvP games</p>
        <p className="font-mono font-semibold text-3xl mt-1">{data.active_games}</p>
      </Card>
      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Pending invites</p>
        <p className="font-mono font-semibold text-3xl mt-1">{data.pending_invites}</p>
      </Card>
      <Card className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">By age band</p>
        <p className="font-mono text-sm mt-1 leading-relaxed">
          Kids: <strong>{data.online_by_band.under_13}</strong><br />
          Teens: <strong>{data.online_by_band.teen}</strong><br />
          Adults: <strong>{data.online_by_band.adult}</strong>
        </p>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Reports queue                                                              //
// --------------------------------------------------------------------------- //
export function ReportsPanel() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports`, {
        headers: authedHeaders(), params: { status_filter: statusFilter },
      });
      setRows(data.reports || []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const resolve = async (id, action) => {
    try {
      await axios.post(`${API}/admin/reports/${id}/resolve`, null, {
        headers: authedHeaders(), params: { action },
      });
      toast.success(`Report resolved (${action}).`);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="admin-reports-panel">
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-xs uppercase tracking-widest">Show</Label>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchRows} className="rounded-full">Refresh</Button>
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Reporter</TableHead>
              <TableHead>Reported</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Loading…</TableCell></TableRow>}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No reports.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r._id}>
                <TableCell className="text-xs whitespace-nowrap">{ago(r.created_at)}</TableCell>
                <TableCell className="font-medium">{r.reporter_username || "—"}</TableCell>
                <TableCell className="font-medium text-destructive">{r.reported_username || "—"}</TableCell>
                <TableCell className="max-w-xs">
                  <p className="text-sm">{r.reason}</p>
                  {r.context && <p className="text-[11px] text-muted-foreground">{r.context}</p>}
                </TableCell>
                <TableCell>
                  <Badge variant={r.status === "pending" ? "default" : "outline"} className="rounded-full">
                    {r.status}{r.review_action ? ` · ${r.review_action}` : ""}
                  </Badge>
                </TableCell>
                <TableCell className="text-right space-x-1">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => resolve(r._id, "dismissed")} data-testid={`report-dismiss-${r._id}`}>Dismiss</Button>
                      <Button size="sm" variant="outline" onClick={() => resolve(r._id, "muted")} data-testid={`report-mute-${r._id}`}><VolumeX className="h-3 w-3 mr-1" />Mute 24h</Button>
                      <Button size="sm" variant="destructive" onClick={() => resolve(r._id, "suspended")} data-testid={`report-suspend-${r._id}`}><ShieldX className="h-3 w-3 mr-1" />Suspend</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Audit log                                                                  //
// --------------------------------------------------------------------------- //
export function AuditPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/audit`, { headers: authedHeaders() })
      .then((r) => setRows(r.data.audit || []))
      .catch(() => {});
  }, []);
  return (
    <div data-testid="admin-audit-panel">
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Audit log is empty.</TableCell></TableRow>
            )}
            {rows.map((a) => (
              <TableRow key={a._id}>
                <TableCell className="text-xs whitespace-nowrap">{fmt(a.created_at)}</TableCell>
                <TableCell>{a.actor_username || <em className="text-muted-foreground">system</em>}</TableCell>
                <TableCell><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{a.action}</code></TableCell>
                <TableCell>{a.target_id || "—"}</TableCell>
                <TableCell className="text-xs">
                  {a.details ? <pre className="whitespace-pre-wrap break-all">{JSON.stringify(a.details)}</pre> : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Announcements                                                              //
// --------------------------------------------------------------------------- //
export function AnnouncementsPanel() {
  const [rows, setRows] = useState([]);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [hours, setHours] = useState(24);

  const fetchRows = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/announcements`, { headers: authedHeaders() });
      setRows(data.announcements || []);
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!message.trim()) return;
    try {
      await axios.post(`${API}/admin/announcements`,
        { message: message.trim(), severity, expires_in_hours: Number(hours) || 24 },
        { headers: authedHeaders() });
      toast.success("Announcement published.");
      setMessage("");
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const drop = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, { headers: authedHeaders() });
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div data-testid="admin-announcements-panel" className="space-y-4">
      <Card className="p-4">
        <p className="font-display text-lg flex items-center gap-2"><Megaphone className="h-4 w-4" /> Publish announcement</p>
        <form onSubmit={create} className="grid grid-cols-1 sm:grid-cols-12 gap-3 mt-3">
          <Textarea
            data-testid="announcement-message"
            value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Tournament this Saturday at 19:00…"
            className="sm:col-span-7"
            rows={3}
          />
          <div className="sm:col-span-3 space-y-2">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger data-testid="announcement-severity" className="rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Label>Expires in (hrs)</Label>
            <Input data-testid="announcement-hours" type="number" value={hours} min={1} max={720} onChange={(e) => setHours(e.target.value)} />
          </div>
          <div className="sm:col-span-2 flex items-end">
            <Button type="submit" data-testid="announcement-publish" className="w-full rounded-full">Publish</Button>
          </div>
        </form>
      </Card>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Message</TableHead>
              <TableHead className="w-28">Severity</TableHead>
              <TableHead className="w-40">Expires</TableHead>
              <TableHead className="w-24">Active</TableHead>
              <TableHead className="text-right w-16">—</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No announcements yet.</TableCell></TableRow>
            )}
            {rows.map((a) => (
              <TableRow key={a._id}>
                <TableCell>{a.message}</TableCell>
                <TableCell><Badge variant={a.severity === "critical" ? "destructive" : "outline"} className="rounded-full">{a.severity}</Badge></TableCell>
                <TableCell className="text-xs">{fmt(a.expires_at)}</TableCell>
                <TableCell>{a.active ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right">
                  {a.active && (
                    <Button size="sm" variant="ghost" onClick={() => drop(a._id)} data-testid={`announcement-delete-${a._id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  User profile dialog                                                        //
// --------------------------------------------------------------------------- //
export function UserProfileDialog({ userId, children }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/users/${userId}/profile`, { headers: authedHeaders() });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const action = async (path, opts = {}) => {
    try {
      await axios.post(`${API}/admin/users/${userId}${path}`, null, { headers: authedHeaders(), ...opts });
      toast.success("Done.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const e = data?.extras || {};

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data?.user?.username || "Player"}</DialogTitle>
        </DialogHeader>
        {loading || !data ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Age band" value={e.age_band || "—"} />
              <Stat label="Verified" value={e.age_verified ? "Yes" : "No"} />
              <Stat label="Strikes" value={e.abuse_strikes ?? 0} />
              <Stat label="Blocked chats" value={e.blocked_chat_total ?? 0} />
              <Stat label="Country" value={data.user.country || "—"} />
              <Stat label="Rating" value={(e.rating?.rating ? Math.round(e.rating.rating) : "—")} />
              <Stat label="Games" value={data.games_count} />
              <Stat label="Suspended" value={e.suspended ? (e.suspended_reason || "yes") : "No"} />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => action("/mute", { params: { hours: 24 } })} data-testid="user-action-mute">
                <VolumeX className="h-3.5 w-3.5 mr-1" /> Mute 24h
              </Button>
              <Button size="sm" variant="outline" onClick={() => action("/unmute")} data-testid="user-action-unmute">
                <Volume2 className="h-3.5 w-3.5 mr-1" /> Un-mute
              </Button>
              <Button size="sm" variant="outline" onClick={() => action("/reset-strikes")} data-testid="user-action-reset-strikes">
                Reset strikes
              </Button>
              {!e.suspended ? (
                <Button size="sm" variant="destructive" onClick={() => action("/suspend")} data-testid="user-action-suspend">
                  <ShieldX className="h-3.5 w-3.5 mr-1" /> Suspend
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => action("/unsuspend")} data-testid="user-action-unsuspend">
                  Un-suspend
                </Button>
              )}
            </div>

            <section>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Recent chat (last 50)</p>
              <div className="border rounded-lg max-h-64 overflow-y-auto divide-y divide-border/40">
                {data.recent_chat.length === 0 && <p className="p-3 text-sm text-muted-foreground">No chat messages.</p>}
                {data.recent_chat.map((m) => (
                  <div key={m._id} className={`px-3 py-2 text-xs ${m.blocked ? "bg-destructive/10" : ""}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{m.room === "lobby" ? "Lobby" : `Game ${m.room?.slice(0, 8)}`}</span>
                      <span className="text-muted-foreground">{fmt(m.created_at)}</span>
                    </div>
                    <p>{m.scrubbed || m.text}</p>
                    {m.blocked && (
                      <p className="text-destructive font-medium mt-0.5">
                        Blocked: {m.block_reason}{m.matches?.length ? ` (${m.matches.join(", ")})` : ""}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Reports against this user</p>
              {data.reports_against.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <div className="border rounded-lg divide-y divide-border/40">
                  {data.reports_against.map((r) => (
                    <div key={r._id} className="px-3 py-2 text-xs">
                      <p><strong>{r.reason}</strong> · <span className="text-muted-foreground">{fmt(r.created_at)}</span></p>
                      {r.context && <p className="text-muted-foreground">{r.context}</p>}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Contacts (cross-band)</p>
              {data.contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground">None.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {data.contacts.map((c) => (
                    <Badge key={c.user_id} variant="outline" className="rounded-full">{c.username}</Badge>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <Card className="p-3">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-mono font-semibold text-base mt-1">{value ?? "—"}</p>
    </Card>
  );
}
